/**
 * api2.js
 * API communication and response generation
 * Handles OpenResponses SSE streaming with full event type support
 */

import { elements } from './dom.js?v=302';
import { state } from './state.js?v=302';
import { CONFIG, AGENTS, HERMES_CONFIG } from './config.js?v=302';
import { escapeHtml } from './utils.js?v=302';
import { enhanceCodeBlocks } from './codeBlocks.js?v=302';
import { enhanceLinkPreviews } from './linkPreview.js?v=302';
import { enhanceImages } from './media/imageRenderer.js?v=302';
import { collectGeneratedImageUrls, markdownForGeneratedImages } from './media/generatedImages.js?v=302';
import { enhanceVideoLinks } from './media/youtubeEmbed.js?v=302';
import { showToast, scrollToBottom, announceToScreenReader } from './ui.js?v=302';

import { renderMessage } from './messages.js?v=302';
import { saveChats } from './storage.js?v=302';
import { updateSendButton } from './utils.js?v=302';
import { updateStreamingMarkdown, cancelStreamingMarkdown, rewriteLocalMediaUrls } from './streamingMarkdown.js?v=302';
import { speakText, startStreamingTTS } from './tts.js?v=302';

/**
 * MIME types supported by the OpenClaw input_file API
 */
const SERVER_SUPPORTED_MIMES = {
  'pdf': 'application/pdf',
  'txt': 'text/plain',
  'md': 'text/markdown',
  'html': 'text/html',
  'csv': 'text/csv',
  'json': 'application/json',
};

/**
 * Strip OpenClaw directive tags and untrusted content markers from text.
 * These are internal control tags that should never be shown to the user.
 * @param {string} text - Raw text from the API
 * @returns {string} Cleaned text
 */
function stripDirectiveTags(text) {
  if (!text) return text;
  // Strip [[reply_to_current]], [[reply_to:<id>]], [[audio_as_voice]], [[tts]], [[tts:*]]
  let cleaned = text.replace(/\[\[\s*(?:reply_to_current|reply_to:\s*[^\]]*|audio_as_voice|tts(?::[^\]]*)?)\s*\]\]/g, '');
  // Strip [[tts:text]]...[[/tts:text]] wrappers (keep inner content)
  cleaned = cleaned.replace(/\[\[tts:text\]\]([\s\S]*?)\[\[\/tts:text\]\]/g, '$1');
  // Strip <<<EXTERNAL_UNTRUSTED_CONTENT...>>> markers
  cleaned = cleaned.replace(/<<<EXTERNAL_UNTRUSTED_CONTENT[^>]*>>>/g, '');
  cleaned = cleaned.replace(/<<<\/EXTERNAL_UNTRUSTED_CONTENT[^>]*>>>/g, '');
  // Strip NO_REPLY directive (agent silent reply)
  cleaned = cleaned.replace(/^\s*NO_REPLY\s*$/g, '');
  // Strip "No response from OpenClaw." fallback appended by the gateway
  cleaned = cleaned.replace(/\s*No response from OpenClaw\.?\s*$/g, '');
  return cleaned.trim();
}

/**
 * Check if text is a silent/no-reply response from the agent.
 * Handles partial streaming fragments of NO_REPLY too.
 */
function isSilentResponse(text) {
  if (!text) return true;
  const t = text.trim();
  return !t || t === 'NO_REPLY' || 'NO_REPLY'.startsWith(t) || /^No response from OpenClaw\.?$/i.test(t);
}

/**
 * Read a File object as base64 (without the data URL prefix)
 * @param {File} file - File to read
 * @returns {Promise<string>} Base64 encoded string
 */
function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = () => reject(new Error('Failed to read file as base64'));
    reader.readAsDataURL(file);
  });
}

/**
 * URL file extensions the server can fetch and parse via input_file URL source.
 */
const URL_FILE_EXTENSIONS = /\.(pdf|txt|md|html|csv|json)(\?[^#]*)?$/i;

/**
 * URL image extensions the server can fetch via input_image URL source.
 */
const URL_IMAGE_EXTENSIONS = /\.(jpe?g|png|gif|webp)(\?[^#]*)?$/i;

/**
 * MIME type for a URL based on its extension.
 */
function mimeFromUrl(url) {
  const match = url.match(/\.([a-z]+)(\?|$)/i);
  if (!match) return null;
  const ext = match[1].toLowerCase();
  return SERVER_SUPPORTED_MIMES[ext] || { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif', webp: 'image/webp' }[ext] || null;
}

/**
 * Extract document/image URLs from message text.
 * Only extracts URLs that are on their own line (not inline in a sentence),
 * to avoid pulling random links out of a question.
 * @param {string} text - Message text
 * @returns {{ urls: Array<{url: string, type: string}>, cleanText: string }}
 */
function extractDocumentUrls(text) {
  if (!text) return { urls: [], cleanText: text };

  const lines = text.split('\n');
  const urls = [];
  const cleanLines = [];

  for (const line of lines) {
    const trimmed = line.trim();
    // Match a line that is just a URL (possibly with leading/trailing whitespace)
    const urlMatch = trimmed.match(/^(https?:\/\/\S+)$/i);
    if (urlMatch) {
      const url = urlMatch[1];
      if (URL_FILE_EXTENSIONS.test(url)) {
        urls.push({ url, type: 'file' });
        continue; // Don't include in clean text
      } else if (URL_IMAGE_EXTENSIONS.test(url)) {
        urls.push({ url, type: 'image' });
        continue;
      }
    }
    cleanLines.push(line);
  }

  return { urls, cleanText: cleanLines.join('\n') };
}

/**
 * Build the input items array for the OpenResponses API request.
 * Uses input_file for server-supported document types,
 * input_file/input_image URL source for document/image URLs,
 * falls back to inline text for unsupported types.
 * @param {Object} lastMessage - The last user message from the chat
 * @returns {Array} Array of input items for the API
 */
function buildInputItems(lastMessage) {
  if (!lastMessage || lastMessage.role !== "user") return [];

  const contentParts = [];
  const files = lastMessage.files || [];

  // Separate file types
  const imageFiles = files.filter(f => f.isImage && f.base64);
  const serverFiles = files.filter(f => !f.isImage && f.rawFile && SERVER_SUPPORTED_MIMES[f.type]);
  const inlineFiles = files.filter(f => !f.isImage && (!f.rawFile || !SERVER_SUPPORTED_MIMES[f.type]) && f.content);

  // Extract document/image URLs from message text
  const { urls: docUrls, cleanText: messageText } = extractDocumentUrls(lastMessage.content);

  // Build text content: inline file contents + cleaned user message
  let textContent = "";

  if (inlineFiles.length > 0) {
    textContent = inlineFiles.map(file =>
      `--- ATTACHED FILE: ${file.name} ---\n${file.content}\n--- END OF ${file.name} ---\n`
    ).join("\n") + "\n";
  }

  textContent += messageText || "";

  if (textContent.trim()) {
    contentParts.push({ type: "input_text", text: textContent });
  }

  // Add images as input_image (uploaded)
  imageFiles.forEach(file => {
    contentParts.push({
      type: "input_image",
      source: { type: "base64", media_type: file.mimeType, data: file.base64 }
    });
  });

  // Add server-parsed files as input_file (uploaded)
  serverFiles.forEach(file => {
    contentParts.push({
      type: "input_file",
      source: {
        type: "base64",
        media_type: SERVER_SUPPORTED_MIMES[file.type],
        data: file.base64Data,
        filename: file.name
      }
    });
  });

  // Add document URLs as input_file with URL source (server fetches them)
  docUrls.forEach(({ url, type }) => {
    if (type === 'file') {
      const mime = mimeFromUrl(url);
      contentParts.push({
        type: "input_file",
        source: { type: "url", url: url }
      });
    } else if (type === 'image') {
      contentParts.push({
        type: "input_image",
        source: { type: "url", url: url }
      });
    }
  });

  // If only plain text (no multipart content), send as simple message
  if (contentParts.length === 1 && contentParts[0].type === "input_text") {
    return [{
      type: "message",
      role: "user",
      content: contentParts[0].text
    }];
  }

  return [{
    type: "message",
    role: "user",
    content: contentParts
  }];
}

/**
 * Build a user message for the Hermes agent.
 * Returns a string for text-only messages, or an array of content parts
 * (text + image_url) when images are attached.
 */
function buildHermesUserContent(msg) {
  const text = msg.content || '';
  const imageFiles = (msg.files || []).filter(f => f.isImage && f.base64);

  if (imageFiles.length === 0) {
    return text;
  }

  const contentParts = [];
  if (text.trim()) {
    contentParts.push({ type: 'text', text });
  }

  imageFiles.forEach(file => {
    contentParts.push({
      type: 'image_url',
      image_url: {
        url: `data:${file.mimeType};base64,${file.base64}`
      }
    });
  });

  return contentParts;
}

function createSseEventParser(onEvent) {
  let buffer = "";

  const emitBlock = (block) => {
    if (!block.trim()) return;

    let eventType = "";
    const dataLines = [];

    for (const rawLine of block.split("\n")) {
      if (!rawLine || rawLine.startsWith(":")) continue;

      const colonIndex = rawLine.indexOf(":");
      const field = colonIndex === -1 ? rawLine : rawLine.slice(0, colonIndex);
      let value = colonIndex === -1 ? "" : rawLine.slice(colonIndex + 1);
      if (value.startsWith(" ")) value = value.slice(1);

      if (field === "event") {
        eventType = value.trim();
      } else if (field === "data") {
        dataLines.push(value);
      }
    }

    if (dataLines.length === 0) return;
    onEvent({ eventType, data: dataLines.join("\n") });
  };

  return {
    push(chunk) {
      buffer += chunk;
      buffer = buffer.replace(/\r\n/g, "\n").replace(/\r/g, "\n");

      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        emitBlock(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        boundary = buffer.indexOf("\n\n");
      }
    },
    flush() {
      if (buffer.trim()) emitBlock(buffer);
      buffer = "";
    }
  };
}

function createStreamPresenter(contentDiv, { isNearBottom, scheduleScrollToBottom }) {
  let targetText = "";
  let renderedText = "";
  let rafId = null;
  let lastFrame = 0;

  const render = () => {
    if (!contentDiv || !contentDiv.isConnected || !renderedText.trim()) return;
    const stickToBottom = isNearBottom();
    try {
      updateStreamingMarkdown(contentDiv, renderedText);
    } catch (e) {
      contentDiv.textContent = renderedText;
    }
    if (stickToBottom) scheduleScrollToBottom();
  };

  const schedule = () => {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(tick);
  };

  const tick = (now) => {
    rafId = null;
    if (!contentDiv || !contentDiv.isConnected) return;

    if (targetText.length < renderedText.length) {
      renderedText = targetText;
    }

    const backlog = targetText.length - renderedText.length;
    if (backlog <= 0) return;

    const elapsed = lastFrame ? Math.min(48, Math.max(12, now - lastFrame)) : 16;
    lastFrame = now;

    let charsPerSecond = 95;
    if (backlog > 1200) charsPerSecond = 900;
    else if (backlog > 450) charsPerSecond = 520;
    else if (backlog > 160) charsPerSecond = 260;

    const charBudget = Math.max(1, Math.min(backlog, Math.ceil(charsPerSecond * elapsed / 1000)));
    renderedText += targetText.slice(renderedText.length, renderedText.length + charBudget);
    render();

    if (renderedText.length < targetText.length) schedule();
  };

  return {
    setTarget(text) {
      targetText = text || "";
      schedule();
    },
    flush(text = targetText) {
      targetText = text || "";
      renderedText = targetText;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      render();
    },
    cancel() {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    }
  };
}

/**
 * Generate AI response for a chat
 * Handles streaming and error handling with full OpenResponses event support
 * @param {Object} chat - Chat object containing messages
 */
export async function generateResponse(chat) {
  state.isGenerating = true;
  updateSendButton();

  // Show typing indicator
  const typingWrapper = document.createElement("div");
  typingWrapper.className = "message-wrapper bot";
  typingWrapper.id = "typing-indicator";
  typingWrapper.innerHTML = `
            <div class="typing-indicator">
                <div class="dot"></div>
                <div class="dot"></div>
                <div class="dot"></div>
            </div>
        `;
  elements.messagesContainer.appendChild(typingWrapper);
  scrollToBottom();
  announceToScreenReader(`Generating response from ${state.model || 'AI model'}...`);

  // Declare contentDiv in outer scope for error handler access
  let contentDiv = null;

  try {
    const lastMessage = chat.messages[chat.messages.length - 1];
    const inputItems = buildInputItems(lastMessage);

    state.controller = new AbortController();

    // Session user ID for server-side session routing.
    // Includes a title slug for new chats so sessions are identifiable in Control UI.
    // e.g. "marino-webchat-check-my-calendar-1771939290"
    let sessionUser = CONFIG.user;
    if (state.currentChatId) {
      // Use stored sessionUser if already established (preserves existing sessions)
      if (chat.sessionUser) {
        sessionUser = chat.sessionUser;
      } else {
        // New chat: generate a readable session user with title slug
        const titleSlug = (chat.title || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
          .substring(0, 30);
        sessionUser = titleSlug
          ? `${CONFIG.user}-${titleSlug}-${state.currentChatId}`
          : `${CONFIG.user}-${state.currentChatId}`;
        // Persist so subsequent messages in this chat use the same session
        chat.sessionUser = sessionUser;
      }
    }

    // Resolve the active agent
    const activeAgent = AGENTS.find(a => a.id === state.selectedAgent) || AGENTS[0];

    // Build request based on agent type
    const isHermesAgent = activeAgent.id === 'hermes';
    
    let requestBody;
    let apiUrl;
    let headers;
    
    if (isHermesAgent) {
      // Convert chat history to Chat Completions format
      const messages = [];
      for (const msg of chat.messages) {
        if (msg.role === 'user') {
          messages.push({ role: 'user', content: buildHermesUserContent(msg) });
        } else if (msg.role === 'assistant') {
          messages.push({ role: 'assistant', content: msg.content || '' });
        }
      }
      // If no messages yet (first turn), use the last message
      if (messages.length === 0 && lastMessage) {
        messages.push({ role: 'user', content: buildHermesUserContent(lastMessage) });
      }
      
      requestBody = {
        model: HERMES_CONFIG.model || 'hermes-agent',
        messages: messages,
        stream: true,
      };
      apiUrl = HERMES_CONFIG.apiUrl;
      headers = {
        "Authorization": `Bearer ${HERMES_CONFIG.apiKey}`,
        "Content-Type": "application/json",
        "X-Webchat-Agent-Id": activeAgent.id,
      };
    } else {
      requestBody = {
        model: activeAgent.model,
        user: sessionUser,
        input: inputItems,
        stream: true,
      };
      apiUrl = CONFIG.apiUrl;
      headers = {
        "Authorization": `Bearer ${CONFIG.authToken}`,
        "Content-Type": "application/json",
        "x-openclaw-agent-id": activeAgent.id,
        "x-openclaw-scopes": "operator.write",
      };
    }

    // Retry logic for initial connection failures
    const MAX_RETRIES = 3;
    const RETRY_DELAYS = [1000, 3000, 6000];
    let response;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        if (attempt > 0) {
          showToast(`Reconnecting... (attempt ${attempt}/${MAX_RETRIES})`, "info");
          await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt - 1]));
          if (state.controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
        }

        response = await fetch(apiUrl, {
          method: "POST",
          headers,
          body: JSON.stringify(requestBody),
          signal: state.controller.signal,
        });
        break;
      } catch (fetchError) {
        if (fetchError.name === 'AbortError') throw fetchError;
        if (attempt === MAX_RETRIES) {
          showToast("Connection failed after retries", "error");
          throw fetchError;
        }
      }
    }

    if (!response.ok) {
      const errorBody = await response.text();
      let detail = "";
      try {
        const errJson = JSON.parse(errorBody);
        detail = errJson.error?.message || errJson.message || errJson.detail || errorBody;
      } catch {
        detail = errorBody;
      }
      throw new Error(`API Error ${response.status}: ${detail}`);
    }

    const typingIndicator = document.getElementById("typing-indicator");
    let typingRemoved = false;

    const botMessage = {
      id: Date.now().toString(),
      role: "assistant",
      content: "",
      timestamp: new Date().toISOString(),
      usage: null,
      responseId: null,
    };

    chat.messages.push(botMessage);
    const prevMsg = chat.messages.length > 1 ? chat.messages[chat.messages.length - 2] : null;
    const wrapper = renderMessage(botMessage, prevMsg);
    contentDiv = wrapper.querySelector(".message-content");
    wrapper.classList.add("streaming");

    const scrollContainer = elements.messagesContainer;
    const isNearBottom = () =>
      scrollContainer.scrollTop + scrollContainer.clientHeight >=
      scrollContainer.scrollHeight - 140;
    let scrollRafId = null;
    const scheduleScrollToBottom = () => {
      if (scrollRafId !== null) return;
      scrollRafId = requestAnimationFrame(() => {
        scrollRafId = null;
        scrollToBottom();
      });
    };

    scrollToBottom();

    // SSE stream processing
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let botText = "";
    let citations = [];
    let streamDisconnected = false;
    let responseFailed = false;
    let responseError = null;
    let currentOutputItemId = null;
    let outputItemTexts = {};       // Track text per output item ID
    let thinkingItemIds = new Set(); // Track which items are thinking/reasoning
    let ttsStream = state.voiceMode ? startStreamingTTS() : null;
    const generatedImageUrls = new Set();
    const presenter = createStreamPresenter(contentDiv, { isNearBottom, scheduleScrollToBottom });

    const removeTypingIndicator = () => {
      if (!typingRemoved && typingIndicator) {
        typingIndicator.remove();
        typingRemoved = true;
      }
    };

    const appendVisibleText = (text) => {
      if (!text) return;
      botText += text;
      if (isSilentResponse(botText)) return;
      removeTypingIndicator();
      if (ttsStream) ttsStream.feed(text);
      presenter.setTarget(botText);
    };

    const adoptVisibleText = (nextText) => {
      if (!nextText || isSilentResponse(nextText)) return;
      const previousText = botText;
      botText = nextText;
      removeTypingIndicator();
      if (ttsStream && botText.startsWith(previousText)) {
        const newTail = botText.slice(previousText.length);
        if (newTail) ttsStream.feed(newTail);
      }
      presenter.setTarget(botText);
    };

    const appendGeneratedImages = (eventPayload) => {
      const newUrls = collectGeneratedImageUrls(eventPayload)
        .filter((url) => !generatedImageUrls.has(url));
      if (newUrls.length === 0) return false;

      newUrls.forEach((url) => generatedImageUrls.add(url));
      const imageMarkdown = markdownForGeneratedImages(newUrls);
      botText += (botText.trim() ? "\n\n" : "") + imageMarkdown;
      removeTypingIndicator();
      presenter.setTarget(botText);
      return true;
    };

    const extractText = (value) => {
      if (!value) return "";
      if (typeof value === "string") return value;
      if (typeof value?.text === "string") return value.text;
      if (Array.isArray(value)) {
        return value.map((v) => extractText(v)).filter(Boolean).join("");
      }
      if (Array.isArray(value?.content)) {
        return value.content
          .map((v) => {
            if (v?.type === "output_text" || typeof v?.text === "string") return extractText(v);
            if (Array.isArray(v?.content)) {
              return v.content.map((vv) => extractText(vv)).filter(Boolean).join("");
            }
            return "";
          })
          .filter(Boolean)
          .join("");
      }
      return "";
    };

    const handleSseEvent = ({ eventType: parsedEventType, data }) => {
      const trimmedData = data.trim();
      if (!trimmedData || trimmedData === "[DONE]") return;

      try {
        const json = JSON.parse(data);

        // Route based on event type or json.type
        const eventType = parsedEventType || json.type || "";
        appendGeneratedImages(json);

        switch (eventType) {
          case "response.created":
            // Capture response ID for tracking
            if (json.response?.id) {
              botMessage.responseId = json.response.id;
            }
            break;

          case "response.output_text.delta": {
            // Streaming text content
            const delta = json.delta || "";
            if (delta) {
              // Skip deltas belonging to thinking/reasoning items
              if (currentOutputItemId && thinkingItemIds.has(currentOutputItemId)) break;
              // Track per-item text
              if (currentOutputItemId) {
                outputItemTexts[currentOutputItemId] = (outputItemTexts[currentOutputItemId] || "") + delta;
              }
              appendVisibleText(delta);
            }
            break;
          }

          case "response.output_text.done":
            // Final complete text for this content part.
            // Only adopt if it's substantive and longer than what we have,
            // to avoid overwriting already-streamed content with a subset.
            if (json.text) {
              const candidateText = stripDirectiveTags(json.text);
              if (candidateText && candidateText.length >= botText.length && candidateText !== botText) {
                adoptVisibleText(candidateText);
              }
            }
            break;

          case "response.output_item.added": {
            // Track new output items and detect thinking/reasoning types
            const addedItem = json.item || json.output_item || {};
            const itemId = addedItem.id || `item-${Date.now()}`;
            currentOutputItemId = itemId;
            const itemType = addedItem.type || "";
            // Detect thinking/reasoning output items (varies by model)
            if (itemType === "reasoning" || itemType === "thinking" ||
                addedItem.role === "reasoning" || addedItem.role === "thinking") {
              thinkingItemIds.add(itemId);
            }
            break;
          }

          case "response.output_item.done": {
            const doneItem = json.item || json.output_item || {};
            const itemId = doneItem.id || currentOutputItemId;
            const itemType = doneItem.type || "";

            // If this is a thinking/reasoning item completing, skip it
            if (thinkingItemIds.has(itemId) || itemType === "reasoning" || itemType === "thinking" ||
                doneItem.role === "reasoning" || doneItem.role === "thinking") {
              // Remove any thinking text that was accidentally streamed into botText
              const thinkingText = outputItemTexts[itemId] || "";
              if (thinkingText && botText.startsWith(thinkingText)) {
                botText = botText.slice(thinkingText.length).trimStart();
                presenter.setTarget(botText);
              }
              break;
            }

            const candidateText = extractText(doneItem) || extractText(json.text) || extractText(json.content);

            if (candidateText) {
              const cleaned = stripDirectiveTags(candidateText);
              // Only adopt if it's at least as long — never shrink botText
              if (cleaned && !isSilentResponse(cleaned) && cleaned.length >= botText.length) {
                adoptVisibleText(cleaned);
              }
            }
            break;
          }

          case "response.content_part.added":
          case "response.content_part.done": {
            const part = json.part || json.content_part || {};

            // Skip thinking/reasoning content parts
            if (part.type === "reasoning" || part.type === "thinking") break;

            const candidateText = extractText(part) || extractText(json.text) || extractText(json.content);

            if (candidateText) {
              const cleaned = stripDirectiveTags(candidateText);
              if (cleaned && !isSilentResponse(cleaned) && !botText.includes(cleaned)) {
                appendVisibleText(cleaned);
              }
            }
            break;
          }

          case "response.completed":
            // Capture usage data and final response info
            if (json.response?.usage) {
              botMessage.usage = json.response.usage;
            }
            if (json.response?.id) {
              botMessage.responseId = json.response.id;
            }
            break;

          case "response.failed":
            // Structured error from the API
            responseFailed = true;
            responseError = json.response?.error || json.error || { message: "Unknown error" };
            break;

          default:
            // Handle legacy/fallback formats
            if (json.error) {
              responseFailed = true;
              responseError = json.error;
            } else if (json.choices?.[0]?.delta?.content) {
              // Chat completions fallback
              appendVisibleText(json.choices[0].delta.content || "");
            } else if (json.choices?.[0]?.message?.content) {
              adoptVisibleText(json.choices[0].message.content || "");
            } else if (json.usage) {
              botMessage.usage = json.usage;
            }
            break;
        }

        // Capture citations from any event
        if (json.citations && Array.isArray(json.citations)) {
          json.citations.forEach((c) => {
            if (c.type === "url_citation" && c.url_citation) {
              citations.push(c.url_citation);
            }
          });
        }
      } catch (parseError) {
        // Skip malformed JSON chunks.
      }
    };

    const sseParser = createSseEventParser(handleSseEvent);

    while (true) {
      let readResult;
      try {
        readResult = await reader.read();
      } catch (streamError) {
        if (streamError.name === 'AbortError') throw streamError;
        streamDisconnected = true;
        break;
      }
      const { done, value } = readResult;
      if (done) break;

      sseParser.push(decoder.decode(value, { stream: true }));
    }
    sseParser.push(decoder.decode());
    sseParser.flush();

    // Handle response.failed
    if (responseFailed && responseError) {
      if (!typingRemoved && typingIndicator) {
        typingIndicator.remove();
        typingRemoved = true;
      }
      const errMsg = responseError.message || "The request failed";
      throw new Error(errMsg);
    }

    // Handle stream disconnection
    if (streamDisconnected) {
      if (!typingRemoved && typingIndicator) {
        typingIndicator.remove();
        typingRemoved = true;
      }
      if (botText.trim()) {
        botText += "\n\n---\n*⚠️ Connection lost. Response may be incomplete.*";
        showToast("Connection lost. Partial response saved.", "error");
      } else {
        showToast("Connection lost before response arrived. Try again.", "error");
      }
    }

    // Strip directive tags from final text, but preserve streamed content
    // if stripping would blank it out entirely
    const preStripText = botText;
    botText = stripDirectiveTags(botText);
    if (!botText.trim() && preStripText.trim()) {
      // Directive stripping wiped the content — keep the original
      botText = preStripText;
    }

    // Format citations
    if (citations.length > 0) {
      const seenUrls = new Set();
      const uniqueCitations = [];
      for (const c of citations) {
        if (c.url && !seenUrls.has(c.url)) {
          seenUrls.add(c.url);
          uniqueCitations.push(c);
        }
      }
      const finalCitations = uniqueCitations.slice(0, 5);
      if (finalCitations.length > 0) {
        botText += "\n\n**Sources:**\n";
        finalCitations.forEach((c, i) => {
          try {
            const hostname = new URL(c.url).hostname;
            botText += `${i + 1}. [${hostname}](${c.url})\n`;
          } catch (e) {
            botText += `${i + 1}. [Source](${c.url})\n`;
          }
        });
      }
    }

    const missingGeneratedImages = Array.from(generatedImageUrls)
      .filter((url) => !botText.includes(url));
    if (missingGeneratedImages.length > 0) {
      botText += (botText.trim() ? "\n\n" : "") + markdownForGeneratedImages(missingGeneratedImages);
    }

    const stickToBottom = isNearBottom();
    presenter.flush(botText);

    // Cancel streaming renderer and do final authoritative parse
    cancelStreamingMarkdown(contentDiv);

    // Handle silent/empty responses (e.g. NO_REPLY from the agent).
    // Show a subtle acknowledgment so the user knows the gateway is alive.
    if (isSilentResponse(botText)) {
      botText = "*(acknowledged)*";
    }

    botMessage.content = botText;
    wrapper.classList.remove("streaming");

    // Clear streaming-era idempotency flags so final render processes all media
    delete contentDiv.dataset.videoEnhanced;

    try {
      let parsedHtml = rewriteLocalMediaUrls(window.marked.parse(botText));
      contentDiv.innerHTML = parsedHtml;
      window.Prism.highlightAllUnder(contentDiv);
      enhanceCodeBlocks(contentDiv);
      enhanceLinkPreviews(contentDiv);
      enhanceImages(contentDiv);
      enhanceVideoLinks(contentDiv);
    } catch (e) {
      contentDiv.textContent = botText;
    }

    if (stickToBottom) {
      scrollToBottom();
    }

    // Update meta with usage data if available (arrived via response.completed)
    if (botMessage.usage && wrapper) {
      const metaEl = wrapper.querySelector(".message-meta");
      if (metaEl) {
        const output = botMessage.usage.output_tokens || botMessage.usage.completion_tokens || 0;
        const total = botMessage.usage.total_tokens || 0;
        const input = total > output ? total - output : (botMessage.usage.input_tokens || botMessage.usage.prompt_tokens || 0);

        const fmtK = (n) => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : n.toLocaleString();

        const usageSpan = document.createElement("span");
        usageSpan.className = "meta-usage";
        usageSpan.innerHTML = `| <span class="meta-usage-label">In</span> <span class="meta-usage-value">[${fmtK(input)}]</span>
          <span class="meta-usage-label">Out</span> <span class="meta-usage-value">[${fmtK(output)}]</span>`;

        const actionsDiv = metaEl.querySelector(".message-actions");
        if (actionsDiv) {
          metaEl.insertBefore(usageSpan, actionsDiv);
        } else {
          metaEl.appendChild(usageSpan);
        }
      }
    }

    announceToScreenReader(`Response received from ${state.model || 'AI model'}`);
    saveChats();

    if (ttsStream) {
      ttsStream.finish();
    }
  } catch (error) {
    const typingIndicator = document.getElementById("typing-indicator");
    if (typingIndicator) typingIndicator.remove();

    let errorMsg = error.message;

    if (error.name === "AbortError") {
      try { cancelStreamingMarkdown(contentDiv); } catch(e) {}
      showToast("Generation cancelled", "info");
      return;
    }

    if (error.message.includes("Failed to fetch") || error.message.includes("network")) {
      errorMsg = "Network error. Please check your internet connection and try again.";
    } else if (error.message.includes("401") || error.message.includes("Unauthorized")) {
      errorMsg = "Invalid API key or authentication error.";
    } else if (error.message.includes("402") || error.message.includes("Payment")) {
      errorMsg = "Payment required. Please check your account status.";
    } else if (error.message.includes("429") || error.message.includes("Too Many")) {
      errorMsg = "Rate limit exceeded. Please wait a moment and try again.";
    }

    const errorMessage = {
      id: Date.now().toString(),
      role: "assistant",
      content: `Error: ${escapeHtml(errorMsg)}\n\n**Troubleshooting:**\n- Check your internet connection\n- Try refreshing the page\n- If the problem persists, the server may be temporarily unavailable`,
      timestamp: new Date().toISOString(),
    };

    chat.messages.push(errorMessage);
    const prevMsg = chat.messages.length > 1 ? chat.messages[chat.messages.length - 2] : null;
    renderMessage(errorMessage, prevMsg);
    announceToScreenReader(`Error: ${errorMsg}`);
    showToast("Error: " + errorMsg.substring(0, 50), "error");
  } finally {
    try {
      const streamingWrapper = contentDiv?.closest?.(".message-wrapper");
      streamingWrapper?.classList.remove("streaming");
    } catch (e) {}
    state.isGenerating = false;
    state.controller = null;
    const typingIndicator = document.getElementById("typing-indicator");
    if (typingIndicator) typingIndicator.remove();
    updateSendButton();
  }
}
